import { useState } from 'react';
import { supabase } from '../lib/supabase';

export interface Product {
  id: string;
  vendedor_id: string;
  titulo: string;
  descripcion: string;
  precio: number;
  categoria: string;
  estado: 'nuevo' | 'usado';
  pueblo: string;
  fotos: string[];
  es_carga_pesada: boolean;
  created_at: string;
}

export interface ProductFilters {
  categoria?: string;
  pueblo?: string;
  estado?: string;
  precioMin?: number;
  precioMax?: number;
  busqueda?: string;
}

export const useProducts = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createProduct = async (productData: Omit<Product, 'id' | 'created_at' | 'vendedor_id'>) => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Debes iniciar sesión para publicar productos');
      }

      const { data, error: insertError } = await supabase
        .from('products')
        .insert([
          {
            ...productData,
            vendedor_id: user.id,
          },
        ])
        .select()
        .maybeSingle();

      if (insertError) throw insertError;

      setLoading(false);
      return { data, error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Error al crear el producto';
      setError(errorMessage);
      setLoading(false);
      return { data: null, error: errorMessage };
    }
  };

  const getProducts = async (filters?: ProductFilters) => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.categoria && filters.categoria !== 'todas') {
        query = query.eq('categoria', filters.categoria);
      }

      if (filters?.pueblo && filters.pueblo !== 'todos') {
        query = query.eq('pueblo', filters.pueblo);
      }

      if (filters?.estado && filters.estado !== 'todos') {
        query = query.eq('estado', filters.estado);
      }

      if (filters?.precioMin !== undefined) {
        query = query.gte('precio', filters.precioMin);
      }

      if (filters?.precioMax !== undefined) {
        query = query.lte('precio', filters.precioMax);
      }

      if (filters?.busqueda) {
        query = query.or(`titulo.ilike.%${filters.busqueda}%,descripcion.ilike.%${filters.busqueda}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setLoading(false);
      return { data: data as Product[], error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Error al obtener productos';
      setError(errorMessage);
      setLoading(false);
      return { data: null, error: errorMessage };
    }
  };

  const getProductsByVendedor = async (vendedorId: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .eq('vendedor_id', vendedorId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setLoading(false);
      return { data: data as Product[], error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Error al obtener productos del vendedor';
      setError(errorMessage);
      setLoading(false);
      return { data: null, error: errorMessage };
    }
  };

  const getProductById = async (productId: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      setLoading(false);
      return { data: data as Product | null, error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Error al obtener el producto';
      setError(errorMessage);
      setLoading(false);
      return { data: null, error: errorMessage };
    }
  };

  const updateProduct = async (productId: string, updates: Partial<Omit<Product, 'id' | 'vendedor_id' | 'created_at'>>) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: updateError } = await supabase
        .from('products')
        .update(updates)
        .eq('id', productId)
        .select()
        .maybeSingle();

      if (updateError) throw updateError;

      setLoading(false);
      return { data, error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Error al actualizar el producto';
      setError(errorMessage);
      setLoading(false);
      return { data: null, error: errorMessage };
    }
  };

  const deleteProduct = async (productId: string) => {
    setLoading(true);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (deleteError) throw deleteError;

      setLoading(false);
      return { error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Error al eliminar el producto';
      setError(errorMessage);
      setLoading(false);
      return { error: errorMessage };
    }
  };

  return {
    loading,
    error,
    createProduct,
    getProducts,
    getProductsByVendedor,
    getProductById,
    updateProduct,
    deleteProduct,
  };
};